import {
  CellStatuses,
  CellValue,
  DetailedCellError,
  Group,
  IdeasFile,
  RoiFrame,
  RowIdentifier,
} from "@inscopix/ideas-hyperformula";
import { DataTableColumnDefinition } from "../store/DataTableProvider.types";
import { CellErrorLoading } from "../store/engine";
import { isInteger, uniq } from "lodash";
import { readFileCache } from "../cell-value-renderers/CellValueRendererFile";
import { FileStatus } from "types/constants";
import { isValidDateFormat } from "../cell-editors/CellEditorDate";

/**
 * Represents a validation error for a cell value, including an error message
 * and whether it is blocking for analysis execution.
 */
class ValidationError {
  message: string;
  isBlocking: boolean;

  constructor(message: string, isBlocking = true) {
    this.message = message;
    this.isBlocking = isBlocking;
  }
}

/**
 * Recursively flattens a cell value group into an array of member cell values.
 * @param group The group to flatten.
 * @returns An array of all member cell values.
 */
export const flattenCellValueGroup = (
  group: Group,
): Exclude<CellValue, Group>[] => {
  return group.cellValues.flatMap((value) => {
    return value instanceof Group ? flattenCellValueGroup(value) : value;
  });
};

/**
 * Check whether the file extension matches one of the specified file formats.
 * If no file formats are specified, consider the file a match.
 * @param file
 * @param fileFormats
 * @returns `true` if the file is match, `false` otherwise.
 */
export const doesFileMatchFormat = (
  file: { name: string; isSeries: boolean },
  fileFormats: string[],
) => {
  if (!file.isSeries && fileFormats.length > 0) {
    return fileFormats.some((extension) => file.name.endsWith(extension));
  } else {
    return true;
  }
};

type GetValidationErrorsOptions = {
  tableKind: "analysis" | "data";
  columnDefinition: DataTableColumnDefinition;
  isColumnRequired: boolean;
  value: CellValue;
};

/**
 * Get an array of errors describing why a cell value is invalid in a specified
 * column.
 * @param options
 * @returns An array of validation errors.
 */
export const getValidationErrors = (options: GetValidationErrorsOptions) => {
  const { columnDefinition, isColumnRequired, value } = options;
  const isValueLoading =
    value instanceof DetailedCellError &&
    value.message === CellErrorLoading.message;
  const isEmptyValue = value === null || value === "";

  const errors: ValidationError[] = [];

  // Do not display validation errors while async values are loading
  if (isValueLoading) {
    return [];
  }

  if (value instanceof DetailedCellError) {
    if (value.type === "REF") {
      errors.push(
        new ValidationError("Reference to a cell that does not exist"),
      );
    } else if (value.type === "CYCLE") {
      errors.push(new ValidationError("Cell formula contains a cycle"));
    } else {
      errors.push(new ValidationError(value.message));
    }
  }

  // Validate value is non-empty if column is required
  if (isEmptyValue && isColumnRequired) {
    errors.push(new ValidationError("Value is required"));
  }

  // Validate boolean columns
  if (columnDefinition.kind === "boolean" && !isEmptyValue) {
    if (typeof value !== "boolean") {
      errors.push(new ValidationError('Value must be of type "boolean"'));
    }
  }

  // Validate cell status columns
  if (columnDefinition.kind === "cell_status" && !isEmptyValue) {
    if (!(value instanceof CellStatuses)) {
      errors.push(new ValidationError('Value must be of type "cell statuses"'));
    }
  }

  // Validate choice columns
  if (columnDefinition.kind === "choice" && !isEmptyValue) {
    const isValidValue = columnDefinition.choices.some(
      (choice) => choice.value === value,
    );
    if (!isValidValue) {
      const choices = columnDefinition.choices
        .map(({ value }) => value)
        .join(", ");
      errors.push(new ValidationError(`Value must be one of: ${choices}`));
    }
  }

  // Validate date columns
  if (columnDefinition.kind === "date" && !isEmptyValue) {
    if (!isValidDateFormat(value)) {
      errors.push(
        new ValidationError("Value must be a valid date (YYYY-MM-DD)"),
      );
    }
  }

  // Validate file columns
  if (columnDefinition.kind === "file" && !isEmptyValue) {
    // Flatten cell value groups
    const values: Exclude<CellValue, Group>[] =
      value instanceof Group ? flattenCellValueGroup(value) : [value];
    // Extract file cell values
    const files = values.filter((v) => v instanceof IdeasFile) as IdeasFile[];

    // Validate all values are files
    if (files.length !== values.length) {
      errors.push(new ValidationError('Value must be of type "file"'));
    }

    if (
      !columnDefinition.multiple &&
      ((files.length === 1 && files[0].attrs.isSeries) || files.length > 1)
    ) {
      errors.push(new ValidationError("Only one file is allowed"));
    }

    const fileFormats = uniq(columnDefinition.file_formats);

    // Validate all values are the correct type
    const allCorrectType = files.every((f) =>
      doesFileMatchFormat(f.attrs, fileFormats),
    );
    if (!allCorrectType) {
      if (fileFormats.length > 0) {
        const fileExtensions = fileFormats.map((f) => `.${f}`).join(" or ");
        errors.push(
          new ValidationError(
            `File must have extension ${fileExtensions}`,
            false,
          ),
        );
      }
    }

    // Validate all files are the correct status
    const allCorrectStatus = files.every((f) => {
      const cachedFile = readFileCache(f.attrs.id);
      return cachedFile === null || cachedFile.status === FileStatus.AVAILABLE;
    });

    if (!allCorrectStatus && options.tableKind === "analysis") {
      errors.push(new ValidationError("File must be available"));
    }
  }

  // Validate float columns
  if (columnDefinition.kind === "float" && !isEmptyValue) {
    const { min, max } = columnDefinition;

    if (typeof value !== "number" || isNaN(parseFloat(value.toString()))) {
      errors.push(new ValidationError('Value must be of type "float"'));
    }

    if (min !== null && typeof value === "number" && value < min) {
      errors.push(
        new ValidationError(`Value must be greater than or equal to ${min}`),
      );
    }

    if (max !== null && typeof value === "number" && value > max) {
      errors.push(
        new ValidationError(`Value must be less than or equal to ${max}`),
      );
    }
  }

  // Validate identifier columns
  if (columnDefinition.kind === "identifier" && !isEmptyValue) {
    const isGroupOfRowIdentifiers =
      value instanceof Group &&
      flattenCellValueGroup(value)
        // Remove empty values
        .filter((value) => value !== null && value !== "")
        // Check if every value is a row identifier
        .every((value) => value instanceof RowIdentifier);

    if (!(value instanceof RowIdentifier) && !isGroupOfRowIdentifiers) {
      errors.push(
        new ValidationError('Value must be of type "row identifier"'),
      );
    }
  }

  // Validate integer columns
  if (columnDefinition.kind === "integer" && !isEmptyValue) {
    const { min, max } = columnDefinition;

    if (typeof value !== "number" || !isInteger(value)) {
      errors.push(new ValidationError('Value must be of type "integer"'));
    }

    if (min !== null && typeof value === "number" && value < min) {
      errors.push(
        new ValidationError(`Value must be greater than or equal to ${min}`),
      );
    }

    if (max !== null && typeof value === "number" && value > max) {
      errors.push(
        new ValidationError(`Value must be less than or equal to ${max}`),
      );
    }
  }

  // Validate ROI frame columns
  if (columnDefinition.kind === "roi_frame" && !isEmptyValue) {
    if (!(value instanceof RoiFrame)) {
      errors.push(new ValidationError('Value must be of type "ROI frame"'));
    }
  }

  // Validate text columns
  if (columnDefinition.kind === "text" && !isEmptyValue) {
    if (!["boolean", "number", "string"].includes(typeof value)) {
      errors.push(new ValidationError('Value must be of type "text"'));
    }
  }

  return errors;
};
